import { useState, useMemo, useRef, useEffect } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { getLucideIcon, iconNameMap } from "@/shared/lib/lucideIcons";
import { cn } from "@/shared/lib/cn";

interface IconSelectorProps {
  value: string;
  onChange: (icon: string) => void;
  disabled?: boolean;
}

const MAX_RESULTS = 50;

export function IconSelector({ value, onChange, disabled }: IconSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const SelectedIcon = useMemo(() => {
    return value ? getLucideIcon(value) : null;
  }, [value]);

  const filteredIcons = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) {
      return Object.entries(iconNameMap).slice(0, MAX_RESULTS);
    }

    const matches = Object.entries(iconNameMap).filter(
      ([key, { displayName, componentName }]) => {
        const searchKey = key.toLowerCase();
        const searchDisplay = displayName.toLowerCase();
        const searchComponent = componentName.toLowerCase();

        return (
          searchKey.startsWith(query) ||
          searchDisplay.startsWith(query) ||
          searchComponent.startsWith(query) ||
          searchKey.includes(query) ||
          searchDisplay.includes(query)
        );
      }
    );

    matches.sort((a, b) => {
      const aStartsWith =
        a[0].toLowerCase().startsWith(query) ||
        a[1].displayName.toLowerCase().startsWith(query);
      const bStartsWith =
        b[0].toLowerCase().startsWith(query) ||
        b[1].displayName.toLowerCase().startsWith(query);

      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return a[0].localeCompare(b[0]);
    });

    return matches.slice(0, MAX_RESULTS);
  }, [searchQuery]);

  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
    setSearchQuery("");
  };

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) setSearchQuery("");
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-between h-10",
            value &&
              "bg-imperial-50 text-imperial-700 border-imperial-300 hover:bg-imperial-100"
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {SelectedIcon ? (
              <>
                <SelectedIcon className="h-4 w-4 shrink-0" />
                <span className="text-sm capitalize truncate">
                  {iconNameMap[value]?.displayName || value.replace(/-/g, " ")}
                </span>
              </>
            ) : (
              <span className="text-sm text-ink-400">Select an icon...</span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {value && (
              <div
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClear(e);
                  }
                }}
                className="h-5 w-5 rounded p-0.5 hover:bg-grid-100 transition-colors cursor-pointer focus:outline-none focus:bg-imperial-50/50 flex items-center justify-center"
                aria-label="Clear selection"
              >
                <X className="h-3 w-3 text-ink-400" />
              </div>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 p-0" align="start" side="bottom">
        <div className="p-2 border-b border-grid-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <Input
              ref={searchInputRef}
              placeholder="Search icons (e.g., type 'c' for car, customer...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSelect("")}
            className={cn(
              "w-full justify-start h-auto py-2 px-3",
              !value && "bg-grid-100"
            )}
          >
            <span className="text-ink-400">None</span>
          </Button>
          {filteredIcons.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-ink-500">
              No icons found
            </div>
          ) : (
            filteredIcons.map(([iconKey, { displayName }]) => {
              const Icon = getLucideIcon(iconKey);
              if (!Icon) return null;

              const isSelected = value === iconKey;
              return (
                <Button
                  key={iconKey}
                  type="button"
                  variant="ghost"
                  onClick={() => handleSelect(iconKey)}
                  className={cn(
                    "w-full justify-start h-auto py-2 px-3",
                    isSelected && "bg-imperial-50 text-imperial-700"
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="capitalize">{displayName}</span>
                </Button>
              );
            })
          )}
        </div>
        {filteredIcons.length === MAX_RESULTS && searchQuery && (
          <div className="px-3 py-2 border-t border-grid-200 text-xs text-ink-500 text-center">
            Showing first {MAX_RESULTS} results. Type more to refine search.
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
